<script lang="ts">
	import { Verbiage, t } from '@infinitro/content';
	import { parseInlineBoldSegments } from '../parse-inline-bold-segments';
	import { splitSupportChatMessageContent } from '../utils/support-chat-media-url';
	import type { SupportChatMessageAttachment } from '../types';

	export let text = '';
	export let attachments: SupportChatMessageAttachment[] = [];
	/** Local object-URL preview shown until the real (server-hosted) image finishes loading. */
	export let previewUrl: string | undefined = undefined;
	/** Lets the owner drop its record of `previewUrl` once it's no longer needed. */
	export let onPreviewConsumed: (() => void) | undefined = undefined;

	$: parts = attachments.length ? { text, gifUrl: null } : splitSupportChatMessageContent(text);
	$: plainText = parts.text;
	$: mediaUrl = parts.gifUrl;
	$: showText = Boolean(plainText.trim());

	let realImageLoaded = false;

	function handleRealImageSettled(): void {
		realImageLoaded = true;
		if (previewUrl) URL.revokeObjectURL(previewUrl);
		onPreviewConsumed?.();
	}

	$: hasCrossfade = Boolean(previewUrl) && attachments.length === 1;
</script>

{#if showText}
	{#each parseInlineBoldSegments(plainText) as seg, i (i)}
		{#if seg.bold}
			<strong class="bubble-inline-strong">{seg.text}</strong>
		{:else}
			{seg.text}
		{/if}
	{/each}
{/if}

{#if mediaUrl}
	<img
		class="bubble-media"
		class:bubble-media-stacked={showText}
		src={mediaUrl}
		alt={$t(Verbiage.message_icon_alt, 'alts')}
	/>
{/if}

{#each attachments as attachment (attachment.url)}
	{#if hasCrossfade}
		<span class="bubble-media-wrap" class:bubble-media-stacked={showText || mediaUrl}>
			<img
				class="bubble-media bubble-media-crossfade"
				class:bubble-media-crossfade-hidden={realImageLoaded}
				src={previewUrl}
				alt=""
				aria-hidden="true"
			/>
			<img
				class="bubble-media bubble-media-overlay bubble-media-crossfade"
				class:bubble-media-crossfade-hidden={!realImageLoaded}
				src={attachment.url}
				alt={attachment.filename || $t(Verbiage.message_icon_alt, 'alts')}
				on:load={handleRealImageSettled}
				on:error={handleRealImageSettled}
			/>
		</span>
	{:else}
		<img
			class="bubble-media"
			class:bubble-media-stacked={showText || mediaUrl}
			src={attachment.url}
			alt={attachment.filename || $t(Verbiage.message_icon_alt, 'alts')}
		/>
	{/if}
{/each}

<style>
	.bubble-inline-strong {
		font-weight: 600;
	}

	.bubble-media {
		display: block;
		max-height: 12rem;
		max-width: 100%;
		border-radius: 12px;
		-o-object-fit: contain;
		   object-fit: contain;
}

	.bubble-media-wrap {
		position: relative;
		display: block;
}

	.bubble-media-stacked {
		margin-top: 0.5rem;
}

	/*
	 * The preview (normal flow) always stays mounted and keeps sizing the wrapper — the real image
	 * only ever overlays it via opacity, so the box never has to resize when the real image settles.
	 */
	.bubble-media-overlay {
		pointer-events: none;
		position: absolute;
		inset: 0px;
		height: 100%;
		width: 100%;
}

	.bubble-media-crossfade {
		transition: opacity 0.15s ease;
	}

	.bubble-media-crossfade-hidden {
		opacity: 0;
	}
</style>
